import React, { useEffect, useMemo, useState } from "react"
import { RxCross2 } from "react-icons/rx"
import CustomizedMenu from "../muiMenu/muiMenu"
import IconsColor from "../../util/iconsColor"
import type { IFollowUpItem } from "../quotationList/quotationListTypes"
import {
    AddFollowUpFormValues,
    buildFollowUpPutBody,
    formatActivityHistoryDate,
    formatQuotationTimelineTimestamp,
    getFollowUpTypeVisual,
    resolveFollowUpsAfterDelete,
    resolveFollowUpsAfterSave,
    resolveQuotationDownloadedAt,
    sortFollowUpsNewestFirst,
} from "./activityHistoryUtil"
import type { GenericQuotationFollowUpTypeItem, QuotationDownloadItemModel } from "../../models/axiosModel"
import { agent } from "../../apiClient/api"
import { useSnackbar } from "../../context/snackbarContext"
import AddFollowUpPanel from "./AddFollowUpPanel"
import styles from "./activityHistoryPopup.module.scss"

type ActivityHistoryPopupProps = {
    isOpen: boolean
    quotationId: string
    staCode: string
    startedAt?: string | null
    createdAt?: string | null
    quotationDownloads?: QuotationDownloadItemModel[]
    followUps: IFollowUpItem[]
    followUpTypes: GenericQuotationFollowUpTypeItem[]
    allowAllFollowUpTypes: boolean
    onClose: () => void
    onFollowUpsChange?: (quotationId: string, followUps: IFollowUpItem[]) => void
}

const ActivityHistoryPopup: React.FC<ActivityHistoryPopupProps> = ({
    isOpen,
    quotationId,
    staCode,
    startedAt = null,
    createdAt = null,
    quotationDownloads = [],
    followUps,
    followUpTypes,
    allowAllFollowUpTypes,
    onClose,
    onFollowUpsChange,
}) => {
    const { showError, showSuccess } = useSnackbar()
    const [isAddPanelOpen, setIsAddPanelOpen] = useState(false)
    const [editingFollowUp, setEditingFollowUp] = useState<IFollowUpItem | null>(null)
    const [followUpToDelete, setFollowUpToDelete] = useState<IFollowUpItem | null>(null)
    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    const sortedFollowUps = useMemo(
        () => sortFollowUpsNewestFirst(followUps),
        [followUps],
    )

    useEffect(() => {
        if (!isOpen) {
            setIsAddPanelOpen(false)
            setEditingFollowUp(null)
            setFollowUpToDelete(null)
            setIsSaving(false)
            setIsDeleting(false)
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                if (followUpToDelete) {
                    setFollowUpToDelete(null)
                    return
                }
                if (isAddPanelOpen) {
                    setIsAddPanelOpen(false)
                    setEditingFollowUp(null)
                    return
                }
                onClose()
            }
        }

        document.addEventListener("keydown", handleEscape)
        document.body.style.overflow = "hidden"

        return () => {
            document.removeEventListener("keydown", handleEscape)
            document.body.style.overflow = "unset"
        }
    }, [followUpToDelete, isAddPanelOpen, isOpen, onClose])

    if (!isOpen) return null

    const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) {
            onClose()
        }
    }

    const closeAddPanel = () => {
        setIsAddPanelOpen(false)
        setEditingFollowUp(null)
    }

    const handleAddFollowUpClick = () => {
        setEditingFollowUp(null)
        setIsAddPanelOpen(true)
    }

    const handleEditFollowUpClick = (item: IFollowUpItem) => {
        setEditingFollowUp(item)
        setIsAddPanelOpen(true)
    }

    const handleDeleteFollowUpClick = (item: IFollowUpItem) => {
        setFollowUpToDelete(item)
    }

    const handleSaveFollowUp = async (values: AddFollowUpFormValues) => {
        if (!quotationId) {
            showError("Quotation id is missing")
            return
        }

        setIsSaving(true)
        try {
            const response = await agent.QuotationFollowUpAPI.put(
                quotationId,
                buildFollowUpPutBody(values, editingFollowUp?.quotation_follow_up_id),
                true,
            )

            if (!response?.status) {
                throw new Error(response?.error?.message || "Failed to save follow-up")
            }

            const nextFollowUps = resolveFollowUpsAfterSave(response, followUps)
            onFollowUpsChange?.(quotationId, nextFollowUps)
            closeAddPanel()
            showSuccess(editingFollowUp ? "Follow-up updated successfully" : "Follow-up saved successfully")
        } catch (error: any) {
            showError(error?.message || "Failed to save follow-up")
        } finally {
            setIsSaving(false)
        }
    }

    const handleConfirmDeleteFollowUp = async () => {
        const followUpId = followUpToDelete?.quotation_follow_up_id
        if (!followUpId) return

        setIsDeleting(true)
        try {
            const response = await agent.QuotationFollowUpAPI.delete(followUpId, true)

            if (!response?.status) {
                throw new Error(response?.error?.message || "Failed to delete follow-up")
            }

            const nextFollowUps = resolveFollowUpsAfterDelete(followUpId, followUps)
            onFollowUpsChange?.(quotationId, nextFollowUps)
            setFollowUpToDelete(null)
            showSuccess("Follow-up deleted successfully")
        } catch (error: any) {
            showError(error?.message || "Failed to delete follow-up")
        } finally {
            setIsDeleting(false)
        }
    }

    const quotationLabel = staCode ? `Quotation #${staCode}` : "Quotation"
    const downloadedAt = resolveQuotationDownloadedAt(quotationDownloads)

    return (
        <div className={styles.overlay} onClick={handleOverlayClick}>
            <div
                className={[
                    styles.popup,
                    isAddPanelOpen ? styles.popupWithAddPanel : "",
                ].filter(Boolean).join(" ")}
                role="dialog"
                aria-modal="true"
                aria-labelledby="activity-history-title"
                onClick={(event) => event.stopPropagation()}
            >
                <div
                    className={[
                        styles.contentArea,
                        isAddPanelOpen ? styles.contentAreaCollapsed : "",
                    ].filter(Boolean).join(" ")}
                >
                    <div className={styles.header}>
                        <div className={styles.headerTop}>
                            <div className={styles.titleBlock}>
                                <h2 id="activity-history-title" className={styles.title}>
                                    Activity History
                                </h2>
                                <p className={styles.subtitle}>{quotationLabel}</p>
                                <div className={styles.timelineMeta}>
                                    <span>
                                        <span className={styles.timelineMetaLabel}>Started at:</span>
                                        {" "}
                                        {formatQuotationTimelineTimestamp(startedAt)}
                                    </span>
                                    <span>
                                        <span className={styles.timelineMetaLabel}>Created at:</span>
                                        {" "}
                                        {formatQuotationTimelineTimestamp(createdAt)}
                                    </span>
                                    <span>
                                        <span className={styles.timelineMetaLabel}>Download at:</span>
                                        {" "}
                                        {formatQuotationTimelineTimestamp(downloadedAt)}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                className={styles.closeButton}
                                onClick={onClose}
                                aria-label="Close activity history"
                            >
                                <RxCross2 size={16} />
                            </button>
                        </div>
                    </div>

                    <div className={styles.divider} />

                    <div
                        className={[
                            styles.body,
                            sortedFollowUps.length > 3 ? styles.bodyScrollable : "",
                        ].filter(Boolean).join(" ")}
                    >
                        {sortedFollowUps.length === 0 ? (
                            <div className={styles.emptyState}>No activity yet.</div>
                        ) : (
                            <div className={styles.timeline}>
                                {sortedFollowUps.map((item, index) => {
                                    const visual = getFollowUpTypeVisual(
                                        item.quotation_follow_up_type_id,
                                        item.quotation_follow_up_type_name,
                                    )
                                    const Icon = visual.icon
                                    const isLast = index === sortedFollowUps.length - 1

                                    return (
                                        <div
                                            key={item.quotation_follow_up_id}
                                            className={styles.timelineItem}
                                        >
                                            <div className={styles.timelineRail}>
                                                <span
                                                    className={styles.iconCircle}
                                                    style={{
                                                        backgroundColor: visual.backgroundColor,
                                                        color: visual.iconColor,
                                                    }}
                                                >
                                                    <Icon size={16} />
                                                </span>
                                                {!isLast && <span className={styles.connector} />}
                                            </div>
                                            <div className={styles.timelineContent}>
                                                <div className={styles.itemHeader}>
                                                    <span className={styles.typeName}>
                                                        {item.quotation_follow_up_type_name || "Follow-Up"}
                                                    </span>
                                                    <div className={styles.itemMeta}>
                                                        <span className={styles.itemDate}>
                                                            {formatActivityHistoryDate(item.follow_up_date)}
                                                        </span>
                                                        <CustomizedMenu
                                                            buttonText=""
                                                            menuItems={[
                                                                {
                                                                    label: "Edit",
                                                                    icon: (
                                                                        <IconsColor
                                                                            iconName="editIcon"
                                                                            width="22px"
                                                                            height="18px"
                                                                            fillColor="none"
                                                                            strokeColor="#64748B"
                                                                        />
                                                                    ),
                                                                    onClick: () => handleEditFollowUpClick(item),
                                                                },
                                                                {
                                                                    label: "Delete",
                                                                    icon: (
                                                                        <IconsColor
                                                                            iconName="deleteIcon"
                                                                            width="22px"
                                                                            height="18px"
                                                                            strokeColor="#991B1B"
                                                                        />
                                                                    ),
                                                                    onClick: () => handleDeleteFollowUpClick(item),
                                                                    style: { color: "#991B1B" },
                                                                },
                                                            ]}
                                                            buttonVariant="text"
                                                            buttonStyle={{ padding: "0px", minWidth: "0px" }}
                                                            buttonEndIcon={
                                                                <IconsColor
                                                                    iconName="moreVert"
                                                                    width="20"
                                                                    height="20"
                                                                    fillColor="none"
                                                                    strokeColor="#7a7a7a"
                                                                />
                                                            }
                                                            disabled={isAddPanelOpen || Boolean(followUpToDelete)}
                                                            minWidth={140}
                                                        />
                                                    </div>
                                                </div>
                                                {item.remarks && (
                                                    <p className={styles.remarks}>{item.remarks}</p>
                                                )}
                                                {item.created_by && (
                                                    <p className={styles.createdBy}>
                                                        By {item.created_by}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>

                    {!isAddPanelOpen && (
                        <>
                            <div className={styles.divider} />

                            <div className={styles.footer}>
                                <button
                                    type="button"
                                    className={styles.addFollowUpButton}
                                    onClick={handleAddFollowUpClick}
                                    disabled={Boolean(followUpToDelete)}
                                >
                                    + Add Follow-Up
                                </button>
                            </div>
                        </>
                    )}

                    {isAddPanelOpen && (
                        <button
                            type="button"
                            className={styles.contentDimmer}
                            onClick={closeAddPanel}
                            aria-label="Close add follow-up form"
                        />
                    )}
                </div>

                <AddFollowUpPanel
                    isOpen={isAddPanelOpen}
                    followUpTypes={followUpTypes}
                    allowAllFollowUpTypes={allowAllFollowUpTypes}
                    editingFollowUp={editingFollowUp}
                    isSaving={isSaving}
                    onClose={closeAddPanel}
                    onSave={handleSaveFollowUp}
                />
            </div>

            {followUpToDelete && (
                <div
                    className={styles.deleteConfirmOverlay}
                    onClick={(event) => {
                        if (event.target === event.currentTarget && !isDeleting) {
                            setFollowUpToDelete(null)
                        }
                    }}
                >
                    <div
                        className={styles.deleteConfirmDialog}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="delete-follow-up-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <h3 id="delete-follow-up-title" className={styles.deleteConfirmTitle}>
                            Delete Follow-Up
                        </h3>
                        <p className={styles.deleteConfirmText}>
                            Are you sure you want to delete this follow-up
                            {followUpToDelete.quotation_follow_up_type_name
                                ? ` (${followUpToDelete.quotation_follow_up_type_name})`
                                : ""}?
                        </p>
                        <div className={styles.deleteConfirmActions}>
                            <button
                                type="button"
                                className={styles.deleteConfirmCancel}
                                onClick={() => setFollowUpToDelete(null)}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.deleteConfirmDelete}
                                onClick={handleConfirmDeleteFollowUp}
                                disabled={isDeleting}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ActivityHistoryPopup
